import * as THREE from 'three';
import {
  Camera,
  Scene,
  ShaderMaterial,
  Vector2,
  WebGLRenderer,
  WebGLRenderTarget,
  MeshNormalMaterial,
  DepthTexture,
  Vector3,
} from 'three';
import { FullScreenQuad, Pass } from 'three/examples/jsm/postprocessing/Pass';

function getIdRenderMaterial() {
  const result = new ShaderMaterial({
    uniforms: {
      id: { value: 0 },
    },
    defines: {
      MAX_NUM_OBJECTS: 1024,
    },
    vertexShader: `
        #include <morphtarget_pars_vertex>
				#include <skinning_pars_vertex>

				varying vec4 projTexCoord;
				varying vec4 vPosition;
				uniform mat4 textureMatrix;

				void main() {

					#include <skinbase_vertex>
					#include <begin_vertex>
					#include <morphtarget_vertex>
					#include <skinning_vertex>
					#include <project_vertex>

					vPosition = mvPosition;

					vec4 worldPosition = vec4( transformed, 1.0 );

					#ifdef USE_INSTANCING

						worldPosition = instanceMatrix * worldPosition;

					#endif

					worldPosition = modelMatrix * worldPosition;

					projTexCoord = textureMatrix * worldPosition;

				}`,

    fragmentShader: `
        #include <packing>
        uniform int id;

				void main() {
					gl_FragColor = vec4(
            float(id % 256) / 256.0f, 
            float(id % (256*256)) / (256.0f * 256.0f), 
            float(id % (256*256*256)) / (256.0f * 256.0f * 256.0f), 
            1.0f);

				}`,
  });

  result.onBeforeRender = function (renderer, scene, camera, geometry, object) {
    this.uniforms.id.value = object.id;
    this.uniformsNeedUpdate = true;
  };

  return result;
}

function getEdgeDetectionMaterial() {
  return new ShaderMaterial({
    uniforms: {
      normalTexture: { value: null },
      idTexture: { value: null },
      originalTexture: { value: null },
      edgeColor: { value: new Vector3(0.0, 0.0, 0.0) },
      texSize: { value: new Vector2(0.5, 0.5) },
    },
    vertexShader: `
        varying vec2 vUv;

				void main() {
					vUv = uv;
					gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );
				}
    `,
    fragmentShader: `
        varying vec2 vUv;

				uniform sampler2D normalTexture;
        uniform sampler2D idTexture;
        uniform sampler2D originalTexture;
        uniform vec3 edgeColor;
        uniform vec2 texSize;

				void main() {
					vec2 invSize = 1.0 / texSize;
					vec4 uvOffset = vec4(0.5f, 0.0, 0.0, 0.5f) * vec4(invSize, invSize);
					
          vec4 id1 = texture2D( idTexture, vUv + uvOffset.xy);
					vec4 id2 = texture2D( idTexture, vUv - uvOffset.xy);
					vec4 id3 = texture2D( idTexture, vUv + uvOffset.zw);
					vec4 id4 = texture2D( idTexture, vUv - uvOffset.zw);
          
          vec4 nor1 = texture2D( normalTexture, vUv + uvOffset.xy*2.0f);
					vec4 nor2 = texture2D( normalTexture, vUv - uvOffset.xy*2.0f);
					vec4 nor3 = texture2D( normalTexture, vUv + uvOffset.zw*2.0f);
					vec4 nor4 = texture2D( normalTexture, vUv - uvOffset.zw*2.0f);

					float diff1 = length(id1 - id2) * 256.0f;
					float diff2 = length(id3 - id4) * 256.0f;

          float a1 = acos(dot(normalize(nor1 * 2.0f - 1.0f), normalize(nor2 * 2.0f - 1.0f)));
          float a2 = acos(dot(normalize(nor3 * 2.0f - 1.0f), normalize(nor4 * 2.0f - 1.0f)));

          float diff = max(max(diff1, diff2), max(a1, a2));

					gl_FragColor = diff > 0.2 ? vec4(edgeColor, 1.0f) : texture2D(originalTexture, vUv);
				}`,
  });
}

export class AppAdvancedOutlinePass extends Pass {
  private idRenderTarget: WebGLRenderTarget;
  private normalRenderTarget: WebGLRenderTarget;

  private idRenderMaterial: ShaderMaterial;
  private edgeDetectionMaterial: ShaderMaterial;
  private normalRenderMaterial: MeshNormalMaterial;

  private scene: Scene;
  private camera: Camera;
  private resolution: Vector2;
  private edgeColor: THREE.Color;

  private fsQuad: FullScreenQuad;

  constructor(resolution: Vector2, scene: Scene, camera: Camera, edgeColor: THREE.Color) {
    super();

    this.scene = scene;
    this.camera = camera;
    this.resolution = resolution;
    this.edgeColor = edgeColor;

    this.idRenderTarget = new WebGLRenderTarget(resolution.x, resolution.y, {
      format: THREE.RGBAFormat,
    });
    this.idRenderMaterial = getIdRenderMaterial();

    const depthTexture = new DepthTexture(this.resolution.x, this.resolution.y);
    depthTexture.format = THREE.DepthStencilFormat;
    depthTexture.type = THREE.UnsignedInt248Type;

    this.normalRenderTarget = new WebGLRenderTarget(this.resolution.x, this.resolution.y, {
      minFilter: THREE.NearestFilter,
      magFilter: THREE.NearestFilter,
      type: THREE.HalfFloatType,
      depthTexture: depthTexture,
    });

    this.normalRenderMaterial = new MeshNormalMaterial();

    this.edgeDetectionMaterial = getEdgeDetectionMaterial();

    this.fsQuad = new FullScreenQuad();
  }
  setSize(width: number, height: number) {
    this.resolution = new Vector2(width, height);

    this.idRenderTarget.setSize(this.resolution.x, this.resolution.y);
    this.normalRenderTarget.setSize(this.resolution.x, this.resolution.y);
  }

  render(renderer: WebGLRenderer, writeBuffer: WebGLRenderTarget, readBuffer: WebGLRenderTarget): void {
    // Save essential settings to restore state
    const oldAutoClear = renderer.autoClear;
    // Draw mesh index to id render target
    renderer.setRenderTarget(this.idRenderTarget);
    renderer.clear();
    this.scene.overrideMaterial = this.idRenderMaterial;
    renderer.render(this.scene, this.camera);

    // Render mesh normals to render target
    renderer.setRenderTarget(this.normalRenderTarget);
    renderer.clear();
    this.scene.overrideMaterial = this.normalRenderMaterial;
    renderer.render(this.scene, this.camera);

    // Reset render material
    this.scene.overrideMaterial = null;

    // Perform edge detection pass
    renderer.setRenderTarget(this.renderToScreen ? null : writeBuffer);
    this.fsQuad.material = this.edgeDetectionMaterial;
    this.edgeDetectionMaterial.uniforms['idTexture'].value = this.idRenderTarget.texture;
    this.edgeDetectionMaterial.uniforms['normalTexture'].value = this.normalRenderTarget.texture;
    this.edgeDetectionMaterial.uniforms['originalTexture'].value = readBuffer.texture;
    this.edgeDetectionMaterial.uniforms['texSize'].value = this.resolution;
    this.edgeDetectionMaterial.uniforms['edgeColor'].value = this.edgeColor;
    this.fsQuad.render(renderer);

    // Restore the modified render settings
    renderer.autoClear = oldAutoClear;
  }
  dispose(): void {
    this.idRenderMaterial.dispose();
    this.normalRenderMaterial.dispose();

    this.idRenderTarget.dispose();
    this.normalRenderTarget.dispose();

    this.edgeDetectionMaterial.dispose();

    this.fsQuad.dispose();
  }
}
